import { PublicClientApplication, type Configuration, type SilentRequest } from '@azure/msal-browser'
import { crearRenovadorDeToken } from './reautenticacion'

/**
 * Autenticación contra Microsoft Entra External ID (CIAM).
 * SRS: RF-105. Cédula: I-03.
 *
 * Ningún valor de aquí es secreto: son identificadores públicos de la SPA.
 * El token nunca se guarda en localStorage para reducir el riesgo de robo por XSS.
 */
const configuracion: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_ENTRA_CLIENT_ID,
    authority: import.meta.env.VITE_ENTRA_AUTHORITY,
    knownAuthorities: [new URL(import.meta.env.VITE_ENTRA_AUTHORITY).hostname],
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
}

export const msal = new PublicClientApplication(configuracion)

export const alcanceApi = import.meta.env.VITE_ENTRA_API_SCOPE

export const peticionDeAcceso = {
  scopes: [alcanceApi],
}

/**
 * Id del contexto de autenticación (Acceso Condicional) que el tenant asocia a "requiere MFA".
 * Debe coincidir con Seguridad:IdDeContextoMfa del API y con el contexto creado en el portal.
 */
const idDeContextoMfa = import.meta.env.VITE_ENTRA_ID_CONTEXTO_MFA ?? 'c1'

const claimsDeContextoMfa = JSON.stringify({
  access_token: { acrs: { essential: true, value: idDeContextoMfa } },
})

/**
 * MSAL cachea por separado un token pedido con 'claims' de uno pedido sin él. Sin marcar que
 * ya se satisfizo el reto, cada renovación silenciosa posterior a pedirSegundoFactor() volvería
 * a traer un token SIN 'acrs' — el API respondería MFA_REQUERIDO otra vez y la SPA entraría en
 * un loop de redirects contra Entra (RNF-05 / I-04).
 */
const claveMfaSatisfecho = 'contaquick.mfaSatisfecho'

/**
 * M2 · Marca de UN SOLO USO para avisar al usuario de que su segundo factor quedó atendido.
 *
 * No sirve `claveMfaSatisfecho` para esto: esa se queda en '1' toda la sesión —tiene que
 * quedarse, es lo que evita el loop de redirects—, así que no distingue «acabo de volver» de
 * «volví hace media hora».
 *
 * Y se consume DESDE api.ts, cuando el servidor acepta la primera llamada, no al volver del
 * redirect. La diferencia importa: volver del redirect no prueba nada —el usuario pudo
 * cancelar en la pantalla de Microsoft—, mientras que una respuesta que no es MFA_REQUERIDO sí
 * prueba que el token llegó elevado. Con lo primero, el aviso podría afirmar algo falso justo
 * antes de que la SPA vuelva a mandar al usuario a verificarse.
 */
const claveMfaReciente = 'contaquick.mfaReciente'

/** Lo llama `pedirSegundoFactor` justo antes de salir de la página. */
function anotarSegundoFactorEnCurso(): void {
  sessionStorage.setItem(claveMfaReciente, '1')
}

/**
 * ¿Venimos de atender un segundo factor y todavía no se lo hemos dicho al usuario? Consume la
 * marca, así que la segunda llamada devuelve `false` y el aviso sale una sola vez.
 */
export function consumirSegundoFactorAtendido(): boolean {
  if (sessionStorage.getItem(claveMfaReciente) !== '1') return false
  sessionStorage.removeItem(claveMfaReciente)
  return true
}

/** Si la renovación silenciosa falla, redirige a Entra en vez de mostrar el error (ver reautenticacion.ts). */
const renovarToken = crearRenovadorDeToken(msal, 'contaquick.reautenticacion')

/** Obtiene un token válido, renovándolo en silencio y volviendo a Entra si la sesión venció. */
export async function obtenerToken(): Promise<string> {
  const cuenta = msal.getActiveAccount() ?? msal.getAllAccounts()[0]

  if (!cuenta) {
    throw new Error('No hay sesión iniciada.')
  }

  const yaSatisfizoMfa = sessionStorage.getItem(claveMfaSatisfecho) === '1'
  const peticion: SilentRequest = {
    ...peticionDeAcceso,
    account: cuenta,
    ...(yaSatisfizoMfa ? { claims: claimsDeContextoMfa } : {}),
  }

  return renovarToken(peticion)
}

/**
 * RNF-05 / I-04: el API respondió MFA_REQUERIDO. Pide al usuario el segundo factor mediante un
 * "claims challenge" de Acceso Condicional (reta explícitamente el contexto de autenticación de
 * MFA) y reemplaza la sesión con el token ya elevado. Redirige la página: al volver, MSAL ya
 * trae el nuevo token con 'acrs' satisfecho.
 *
 * Requiere que el tenant tenga creado el contexto de autenticación y su política de Acceso
 * Condicional (ver docs/adr) — sin eso, Entra ignora el reto y el ciclo se repite.
 */
export async function pedirSegundoFactor(): Promise<void> {
  sessionStorage.setItem(claveMfaSatisfecho, '1')
  anotarSegundoFactorEnCurso()
  await msal.acquireTokenRedirect({ ...peticionDeAcceso, claims: claimsDeContextoMfa })
}
